import { useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Group,
  Menu,
  Text,
  Title,
  UnstyledButton,
  VisuallyHidden,
} from "@mantine/core";
import {
  IconArchive,
  IconChevronDown,
  IconDisc,
  IconDownload,
  IconFolder,
  IconListCheck,
  IconLogout,
  IconNews,
  IconPhoto,
  IconSettings,
  IconShield,
  IconUser,
} from "@tabler/icons-react";
import { useLogout, useMe, useNowPlaying, usePublicSettings } from "../api/queries";
import { DEFAULT_AVATAR_URL, atLeast, displayNameOf, type User } from "../api/types";
import { useLink, usePath } from "../router";
import { nativeRuntime } from "../platform/native";
import { isAndroidWeb } from "../platform/androidPopup";
import { Artwork } from "./Artwork";
import { ForgotDialog } from "./ForgotDialog";
import { LoginDialog } from "./LoginDialog";
import { NavBar } from "./NavBar";
import { RegisterDialog } from "./RegisterDialog";
import { SettingsDialog } from "./SettingsDialog";
import { useLayout } from "./useLayout";

/**
 * The two audiences, one badge.
 *
 * Two counts of different things, so each keeps its own icon and its own spelt-out text: the
 * station's covers everyone on jungletrain's own stream, ours covers the people listening
 * through this site, and an unlabelled pair would invite reading the second as a subset of
 * the first. The badge shows the bare numbers; the sentence is the same fact spelt out, which
 * is what the title and a screen reader get. The station's part is dropped when nothing has
 * been polled yet; ours stays at zero, which is a fact about the site and not missing data.
 *
 * `textKey` is a catalogue key and not copy: this is a pure function rendered somewhere else,
 * so the count travels with it and the sentence is built where a `t` exists.
 */
export interface ListenerLine {
  /** Which stream the count is of. Also what the collapse ladder drops first. */
  scope: "station" | "local";
  /** What the badge draws beside the icon. */
  count: number;
  /** A catalogue key taking `count`, not copy -- see above. */
  textKey: string;
}

export function listenerLines(station: number | null, local: number): ListenerLine[] {
  const lines: ListenerLine[] = [];
  if (station !== null && station !== undefined) {
    lines.push({ scope: "station", count: station, textKey: "header.listenersStation" });
  }
  lines.push({ scope: "local", count: local, textKey: "header.listenersLocal" });
  return lines;
}

/**
 * Each count is its own row: the mark and the number centre against each other rather than
 * sharing a baseline, which is what left the icons riding two pixels low inside a pill the
 * flexbox had already centred.
 */
const COUNT_ROW: CSSProperties = { display: "inline-flex", alignItems: "center", gap: 4 };

/**
 * A rule, not a `|`: the glyph sits on the text baseline and reads low beside marks that are
 * centred on the box. Asymmetric margins because the flex gap above already pays the left.
 */
const SEPARATOR: CSSProperties = {
  width: 1,
  height: 11,
  margin: "0 6px 0 3px",
  background: "currentColor",
  opacity: 0.35,
};

/**
 * A real button, stripped of the UA's own chrome.
 *
 * The chevron lives inside the slot's anchor and has to be a control in its own right: a
 * button takes focus, fires on Enter and Space, and carries its name and role on the element
 * instead of in two attributes that have to agree. The reset is what keeps it looking like
 * the bare glyph it is.
 */
const BARE_BUTTON: CSSProperties = {
  appearance: "none",
  background: "none",
  border: 0,
  padding: 0,
  margin: 0,
  font: "inherit",
  color: "inherit",
  cursor: "pointer",
};

export function Header() {
  const { data: me } = useMe();
  const layout = useLayout();
  const { t } = useTranslation();
  const { data: settings } = usePublicSettings();
  const { data: nowPlaying } = useNowPlaying();
  // One piece of state rather than two booleans, so the two dialogs cannot both be open and
  // swapping between them is a single assignment.
  const [dialog, setDialog] = useState<null | "login" | "register" | "forgot" | "settings">(null);
  const path = usePath();
  const inAdmin = path.startsWith("/admin");
  const inReview = path === "/review";
  const inCovers = path === "/covers";
  const inNews = path === "/news" || path.startsWith("/news/");
  const newsEnabled = settings?.news_enabled ?? false;
  // Do not expose News when disabled, even if current path is /news
  const canNews = newsEnabled;
  const homeLink = useLink("/");
  // The three places the chrome offers, each gated on having a write behind it. Kept when it
  // is the one you are in: a control that disappears exactly where it applies leaves the page
  // with no way out of the state it put you in.
  const canReview = !!me && (inReview || atLeast(me.role, "editor"));
  const canCovers = !!me && (inCovers || atLeast(me.role, "admin"));
  const canAdmin = !!me && (inAdmin || atLeast(me.role, "admin"));
  // Which slot is lit, or none of them: `/confirm` and `/reset` are pages the bar does not
  // name, and lighting Archive there would be the bar claiming a place you are not in.
  const section = inAdmin
    ? "/admin"
    : inReview
      ? "/review"
      : inCovers
        ? "/covers"
        : inNews && canNews
          ? "/news"
          : path === "/"
            ? "/"
            : null;
  const slotsCount = (canNews ? 1 : 0) + 1 + (canReview || canCovers ? 1 : 0) + (canAdmin ? 1 : 0);
  const compactControls = layout.phone || layout.twoColumn;
  const compactNav = compactControls && slotsCount >= 3;
  const listeners = nowPlaying
    ? listenerLines(nowPlaying.listeners, nowPlaying.local_listeners)
    : [];
  // Both named because the review slot cancels them: it is a full-height slot in this row,
  // and the only way to reach the row's edges is to know how far its padding is.
  const rowPb = layout.phone ? 10 : 12;
  const rowPt = layout.phone ? "headerTopPhone" : "headerTop";
  // The top padding is `calc(<constant> + env(safe-area-inset-top))`, so subtracting it from
  // the inset leaves exactly the constant, negated. Written against the token rather than as
  // the number beside `rowPb`: that number is a Mantine spacing prop and goes through
  // `--mantine-scale`, the token does not, and the two disagree by a pixel at any scale but 1.
  const rowPtBleed = `calc(var(--android-safe-area-inset-top, env(safe-area-inset-top)) - var(--mantine-spacing-${rowPt}))`;

  /*
   * What gets sacrificed, and in what order.
   *
   * The row does not wrap, so at every width something has to give. The order below, cheapest
   * first. Anything added to this header slots into it rather than jumping the queue:
   *
   *   1. the station's count  -- ours is the number this page is the authority on  (md, 1000)
   *   2. the wordmark and both counts -- on a phone, where the controls need every pixel
   *   3. the account name and crowded section labels -- compact through tablet widths
   *
   * Width-only visibility uses Mantine CSS; compact controls also respect short viewports.
   *
   * Nothing sheds the account menu or the zone: they are the only controls left in the row,
   * and a control that vanishes at a width is a control somebody cannot reach.
   */
  return (
    // `wrap="nowrap"` is not decoration: Mantine's wrapping default would apply and the
    // collapse ladder above would never run -- the row would just fold in two.
    <Group
      wrap="nowrap"
      gap={layout.phone ? 10 : 20}
      // `index.html` sets `viewport-fit=cover`, and the top inset is spent here rather than
      // on the shell so the band's own background fills the notch strip.
      pt={rowPt}
      px={layout.phone ? 12 : 20}
      pb={rowPb}
    >
      <Anchor
        underline="never"
        c="inherit"
        display="flex"
        style={{ alignItems: "center", gap: 12 }}
        {...homeLink}
        aria-label={t("header.home")}
      >
        {/* The mark, at cap height of the word beside it. Its own artwork, so no fill. */}
        <img src="/logo.svg" alt="" aria-hidden="true" style={{ height: 28, width: "auto" }} />
        {/* `lh` is not decoration: this sits in a bar whose height the timeline canvas
            measures, and Mantine's own h1 leading is 1.3, which takes 4px off it. */}
        {!layout.phone && (
          <Title order={1} fz="xl" lh={1.5}>
            jt
            {/* The word carries the mark's two inks: the station's initials in the header's
                own colour, what this site adds to them in the accent. One h1, so the
                accessible name is still "jtarchive" and not two words with a seam. */}
            <Text component="span" inherit c="accent">
              archive
            </Text>
          </Title>
        )}
      </Anchor>

      {/* What is on air is the player's claim and only the player's: it says it once, with
          the artwork and the LIVE word beside the timestamp. The header used to repeat the
          title and badge it a second time, which is two places to read the same fact and two
          places for it to disagree. The counts stay -- nothing else on the page says them. */}
      <Group flex={1} miw={0} gap={10} wrap="nowrap" style={{ overflow: "hidden" }}>
        {!layout.phone && <ListenerCounts listeners={listeners} />}
      </Group>

      {/* Same reason as the row above: the ladder sheds controls, it does not wrap them.
          Stretched so the review slot inside it can be the height of the row: this group is
          the slot's flex parent, and a parent centred on its own content is a ceiling the
          slot cannot bleed past. The controls themselves stay centred -- that is the group's
          own `align`, which stretching the group does not touch. */}
      <Group wrap="nowrap" gap={layout.phone ? 10 : 14} style={{ alignSelf: "stretch" }}>
        <SectionNav
          section={section}
          inCovers={inCovers}
          canNews={canNews}
          canReview={canReview}
          canCovers={canCovers}
          canAdmin={canAdmin}
          compact={compactNav}
          mt={rowPtBleed}
          mb={-rowPb}
        />
        <AndroidDownloadAction />
        <AccountMenu me={me ?? null} compact={compactControls} onDialog={setDialog} />
      </Group>

      {dialog === "login" && !me && (
        <LoginDialog
          onClose={() => setDialog(null)}
          onRegister={() => setDialog("register")}
          onForgot={() => setDialog("forgot")}
        />
      )}
      {dialog === "register" && !me && (
        <RegisterDialog onClose={() => setDialog(null)} onLogin={() => setDialog("login")} />
      )}
      {dialog === "forgot" && !me && (
        <ForgotDialog onClose={() => setDialog(null)} onLogin={() => setDialog("login")} />
      )}
      {dialog === "settings" && <SettingsDialog onClose={() => setDialog(null)} />}
    </Group>
  );
}

/** The pill of listener counts. Nothing polled yet is nothing to draw. */
function ListenerCounts({ listeners }: { listeners: ListenerLine[] }) {
  const { t } = useTranslation();
  if (listeners.length === 0) return null;

  return (
    <Box component="span" fz={10} lh={1.2} style={{ whiteSpace: "nowrap", flex: "none" }}>
      {/* A `Badge` rather than the `StatePill` next door: three of that component's
          four declarations are overridden here anyway. Raised rather than the pill's
          wash, which over the header's own colour is a shape nobody can see, and
          `inline-flex` because Badge is a one-column grid by default -- the three
          parts below would stack. */}
      <Badge
        c="dimmed"
        bg="var(--mantine-color-default)"
        px={7}
        style={{ display: "inline-flex", alignItems: "center" }}
      >
        {/* What the two numbers are counts of, said once at the head rather than
            twice over. Its own rule after it, matching the one between the counts, so
            the glyph reads as a label for the pill and not as a third count. */}
        <Box component="span" aria-hidden="true" style={COUNT_ROW}>
          <IconUser size={10} stroke={1.5} />
          <Box component="span" style={SEPARATOR} />
        </Box>
        {listeners.map((line) => {
          const Icon = line.scope === "station" ? IconDisc : IconFolder;
          const text = t(line.textKey, { count: line.count });
          return (
            <Box
              component="span"
              key={line.scope}
              // The separator below sits inside this span by design, so the rung takes
              // the divider away with the count it separates. Not a filter on the
              // array: that would drop the `title` and the VisuallyHidden text too.
              visibleFrom={line.scope === "station" ? "md" : undefined}
              title={text}
              style={COUNT_ROW}
            >
              <Icon size={10} stroke={1.5} aria-hidden="true" />
              <span aria-hidden="true">{line.count}</span>
              <VisuallyHidden>{text}</VisuallyHidden>
              {line.scope === "station" && (
                <Box component="span" aria-hidden="true" style={SEPARATOR} />
              )}
            </Box>
          );
        })}
      </Badge>
    </Box>
  );
}

/**
 * Where you are in the site. Archive is the page under everything, review is a mode of it,
 * admin is its own tree -- three places you navigate to, so three slots of one shape rather
 * than a link, a chip and a menu item. The bar appears only once there is a choice to make,
 * which for a viewer there never is: a tablist of one that always reads "you are here" is
 * furniture.
 *
 * `stretch` and the two negative margins give the slots the whole row -- top edge to the rule
 * below it -- instead of blocks floating half-way up. Only the constant part of `pt` is
 * cancelled: the rest of it is `env(safe-area-inset-top)`, and a raised block reaching up
 * into the notch strip is not the row.
 */
function SectionNav({
  section,
  inCovers,
  canNews,
  canReview,
  canCovers,
  canAdmin,
  compact,
  mt,
  mb,
}: {
  section: string | null;
  inCovers: boolean;
  canNews: boolean;
  canReview: boolean;
  canCovers: boolean;
  canAdmin: boolean;
  compact: boolean;
  mt: string;
  mb: number;
}) {
  const { t } = useTranslation();
  const newsLink = useLink("/news");
  const homeLink = useLink("/");
  const adminLink = useLink("/admin");
  const reviewLink = useLink("/review");
  const coversLink = useLink("/covers");

  if (!canNews && !canReview && !canAdmin) return null;
  return (
    <NavBar
      label={t("header.sectionsLabel")}
      value={section}
      mt={mt}
      mb={mb}
      style={{ alignSelf: "stretch" }}
    >
      {canNews && (
        <NavBar.Slot
          value="/news"
          href={newsLink.href}
          onClick={newsLink.onClick}
          active={section === "/news"}
          icon={<IconNews size={15} stroke={1.5} aria-hidden="true" />}
          label={t("header.news")}
          compact={compact}
        />
      )}
      <NavBar.Slot
        value="/"
        href={homeLink.href}
        onClick={homeLink.onClick}
        active={section === "/"}
        icon={<IconArchive size={15} stroke={1.5} aria-hidden="true" />}
        label={t("header.archive")}
        compact={compact}
      />
      {/* One slot, two sections. Its identity is derived from the path rather than
          remembered: picking from the menu navigates immediately, so there is no choice
          to hold on to. */}
      {(canReview || canCovers) && (
        <NavBar.Slot
          value={inCovers ? "/covers" : "/review"}
          href={inCovers ? coversLink.href : reviewLink.href}
          onClick={inCovers ? coversLink.onClick : reviewLink.onClick}
          active={section === (inCovers ? "/covers" : "/review")}
          icon={
            inCovers ? (
              <IconPhoto size={15} stroke={1.5} aria-hidden="true" />
            ) : (
              <IconListCheck size={15} stroke={1.5} aria-hidden="true" />
            )
          }
          label={t(inCovers ? "header.covers" : "header.review")}
          compact={compact}
          // Only when there is something to switch to: an editor who is not an admin
          // gets exactly the slot this file has always had.
          rightSection={canReview && canCovers ? <SectionSwitch inCovers={inCovers} /> : undefined}
        />
      )}
      {canAdmin && (
        // The one section that is a colour everywhere else it appears. Arriving in the
        // bar is no reason for it to become the accent every other slot is.
        <NavBar.Slot
          value="/admin"
          href={adminLink.href}
          onClick={adminLink.onClick}
          active={section === "/admin"}
          color="admin"
          icon={<IconShield size={15} stroke={1.5} aria-hidden="true" />}
          label={t("header.admin")}
          compact={compact}
        />
      )}
    </NavBar>
  );
}

/** The chevron inside the Review/Covers slot, and the menu that swaps the two. */
function SectionSwitch({ inCovers }: { inCovers: boolean }) {
  const { t } = useTranslation();
  const reviewLink = useLink("/review");
  const coversLink = useLink("/covers");

  return (
    <Menu position="bottom-start">
      <Menu.Target>
        <Box
          component="button"
          type="button"
          aria-label={t("header.switchSection")}
          title={t("header.switchSection")}
          display="inline-flex"
          style={BARE_BUTTON}
          // The chevron sits inside the slot's own anchor, so both are
          // required: without preventDefault the browser follows the href,
          // without stopPropagation the slot's router click fires. Mantine's
          // target handler still toggles the menu.
          //
          // A real button turns Enter and Space into that same click, so the keyboard
          // path is the pointer path and the menu needs no state of its own here.
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <IconChevronDown size={12} stroke={1.5} aria-hidden="true" />
        </Box>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          component="a"
          href={reviewLink.href}
          onClick={reviewLink.onClick}
          disabled={!inCovers}
          leftSection={<IconListCheck size={15} stroke={1.5} aria-hidden="true" />}
        >
          {t("header.review")}
        </Menu.Item>
        <Menu.Item
          component="a"
          href={coversLink.href}
          onClick={coversLink.onClick}
          disabled={inCovers}
          leftSection={<IconPhoto size={15} stroke={1.5} aria-hidden="true" />}
        >
          {t("header.covers")}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

/**
 * The corner is one control in both states: who you are, and everything that is about you
 * behind it. Signed out that is still true -- "Not logged in" is a fact about the session,
 * and the two ways to start one belong with it rather than loose in a row that a phone cannot
 * spare the width for. Settings ride along either way: the zone in there is about how you
 * read the page, which is the same question.
 */
function AccountMenu({
  me,
  compact,
  onDialog,
}: {
  me: User | null;
  compact: boolean;
  onDialog: (dialog: "login" | "register" | "settings") => void;
}) {
  const { t } = useTranslation();
  const logout = useLogout();
  const runtime = nativeRuntime();
  const downloadsLink = useLink("/downloads");
  const registrationOpen = usePublicSettings().data?.registration_enabled ?? false;

  return (
    <Menu>
      <Menu.Target>
        <UnstyledButton
          aria-label={t("header.account")}
          title={me ? t("header.roleTitle", { role: me.role }) : undefined}
          miw={36}
          mih={36}
        >
          <Group gap={6} wrap="nowrap">
            {me ? (
              <Artwork
                src={me.avatar_url ?? DEFAULT_AVATAR_URL}
                seed={displayNameOf(me)}
                size={20}
                // A circle rather than the tile's rounded square: a face is not a sleeve.
                radius="50%"
                alt={t("header.avatarAlt", { name: displayNameOf(me) })}
              />
            ) : compact ? (
              <IconUser size={20} stroke={1.5} aria-hidden="true" />
            ) : null}
            {/* Signed out the slot still holds something, but it is the absence of a
                name rather than a name -- dim, because what the corner offers instead is
                inside the panel. Both states are regular weight: the name is a label,
                not a heading. */}
            {!compact && (
              <Text
                component="span"
                fz="inherit"
                c={me ? "var(--mantine-color-text)" : "dimmed"}
                maw={180}
                truncate
              >
                {me ? displayNameOf(me) : t("header.notLoggedIn")}
              </Text>
            )}
            <IconChevronDown size={12} stroke={1.5} aria-hidden="true" focusable="false" />
          </Group>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown maw="calc(100vw - 24px)">
        {compact && me && (
          <Menu.Label style={{ overflowWrap: "anywhere" }}>{displayNameOf(me)}</Menu.Label>
        )}
        {runtime && (
          <Menu.Item
            component="a"
            href={downloadsLink.href}
            onClick={downloadsLink.onClick}
            leftSection={<IconDownload size={15} stroke={1.5} aria-hidden="true" />}
          >
            {t("downloads.title")}
          </Menu.Item>
        )}
        {me ? (
          <>
            {/* Neither admin nor review is in here: both are slots in the bar out in the
                row, where what you are in is one control and not a thing to go looking
                for. What is left is about you and how you read the page. */}
            <Menu.Item
              leftSection={<IconSettings size={15} stroke={1.5} aria-hidden="true" />}
              onClick={() => onDialog("settings")}
            >
              {t("header.userSettings")}
            </Menu.Item>
            <Menu.Item
              leftSection={<IconLogout size={15} stroke={1.5} aria-hidden="true" />}
              onClick={() => logout.mutate()}
            >
              {t("header.signOut")}
            </Menu.Item>
          </>
        ) : (
          <>
            <Menu.Item
              leftSection={<IconSettings size={15} stroke={1.5} aria-hidden="true" />}
              onClick={() => onDialog("settings")}
            >
              {t("header.userSettings")}
            </Menu.Item>
            <Menu.Item onClick={() => onDialog("login")}>{t("header.logIn")}</Menu.Item>
            {/* Same condition the login dialog's own link is under: without a relay a
                confirmation cannot be delivered, and an account nobody can activate is
                worse than no button. `/api/settings/public` reports the effective value,
                so this is off when either the flag or the relay is. */}
            {registrationOpen && (
              <Menu.Item onClick={() => onDialog("register")}>{t("header.register")}</Menu.Item>
            )}
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}

/**
 * Direct APK download icon for Android web visitors.
 * Only visible in Android browsers, hidden on desktop, iOS, and inside the native Android app.
 */
export function AndroidDownloadAction() {
  if (!isAndroidWeb()) return null;
  return <AndroidDownloadActionView />;
}

export function AndroidDownloadActionView() {
  const { t } = useTranslation();
  return (
    <ActionIcon
      component="a"
      href="/api/downloads/android/latest"
      download
      variant="subtle"
      color="gray"
      aria-label={t("header.downloadAndroidApp")}
      title={t("header.downloadAndroidApp")}
    >
      <IconDownload size={18} stroke={1.5} aria-hidden="true" />
    </ActionIcon>
  );
}
